import * as React from 'react';
import { AppState } from '../../stores/app';
import { AppService, DomainService } from '../../services/index';
import { Container } from 'typescript-ioc';
import { DomainDto } from '../../dtos/domain';
import { Button, Well } from '@pushly/aqe/lib/components';
import { notification, Skeleton } from 'antd';
import { AppleFilled, CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import {
    FcmSendIntegrationApiVersion,
    IAbstractNativeApnsSendIntegration,
    IAbstractNativeFcmSendIntegration,
    IWebApnsSendIntegration,
    SendIntegrationChannel,
    SendIntegrationPrivateKeyType
} from '../../interfaces/send-integrations';
import {
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration
} from '../../models/send-integration-configurations/native-apns-configurations.model';
import { getClassNames } from '../../_utils/classnames';
import * as moment from 'moment-timezone';
import { convertCase, simpleFormErrorNotification, simpleNotification } from '../../_utils/utils';
import CopyToClipboard from 'react-copy-to-clipboard';
import { WebApnsConfigurationModel } from '../../models/send-integration-configurations/web-apns-configuration.model';
import {
    NativeLegacyFcmConfiguration,
    NativeV1FcmConfiguration
} from '../../models/send-integration-configurations/native-fcm-configurations.model';
import * as randomstring from 'randomstring';
import * as deepEqual from 'deep-equal';
import { SendIntegrationsDrawer } from './send-integrations-editor/send-integrations-drawer';
import { DomainSendIntegrationModel } from '../../models/send-integration-configurations/domain-send-integration.model';
import FirebaseIcon from '../icons/firebase-icon';
import {
    FEAT_CHANNEL_ANDROID,
    FEAT_CHANNEL_IOS,
    FEAT_CHANNEL_NATIVE_APP,
    FEAT_SEND_INT_NATIVE_APNS,
    FEAT_SEND_INT_NATIVE_FCM
} from '../../constants';



interface ISendIntegrationsWellProps {
    domain: DomainDto,
    loading?: boolean,
    title?: string,
    onDomainUpdated: (domain: Partial<DomainDto>) => void,
}

interface ISendIntegrationsWellState {
    showDrawer: boolean,
    drawerKey?: string,
}

export class SendIntegrationsWell extends React.Component<ISendIntegrationsWellProps, ISendIntegrationsWellState> {

    public state: ISendIntegrationsWellState = {
        showDrawer: false,
    };

    protected appState: AppState;
    protected appService: AppService;
    protected domainService: DomainService;

    public constructor(props: ISendIntegrationsWellProps) {
        super(props);

        this.appState = Container.get(AppState);
        this.appService = Container.get(AppService);
        this.domainService = Container.get(DomainService);
    }

    public render() {
        const nativeApnsFlag = this.appState?.flags.findActive(FEAT_CHANNEL_IOS)?.getKey();
        const hasNativeApnsFlagEnabled = this.props.domain?.flags.includes(nativeApnsFlag!);
        const nativeFcmFlag = this.appState?.flags.findActive(FEAT_CHANNEL_ANDROID)?.getKey();
        const hasNativeFcmFlagEnabled = this.props.domain?.flags.includes(nativeFcmFlag!);

        return (
            <Well
                className="nested"
                title="Send Integrations"
                actions={(
                    <Button
                        disabled={this.props.loading}
                        onClick={this.handleDrawerOpen}
                        size="small"
                    >
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                )}
                hideFooter={true}
            >
                <Skeleton
                    loading={this.props.loading}
                    active={true}
                    title={false}
                >
                    {!this.props.loading && (
                        <>
                            <Well
                                title={(
                                    <span>
                                        <AppleFilled style={{ marginRight: '6px' }} />
                                        <span>Apple</span>
                                    </span>
                                )}
                                className="nested"
                                mode="ghost"
                                hideFooter={true}
                            >
                                <ApnsIntegrationWell
                                    apnsConfiguration={this.props.domain?.apnsConfiguration}
                                    channel={SendIntegrationChannel.WEB}
                                />
                                {hasNativeApnsFlagEnabled &&
                                    <ApnsIntegrationWell
                                        apnsConfiguration={this.props.domain?.nativeApnsConfiguration}
                                        channel={SendIntegrationChannel.NATIVE}
                                    />
                                }
                            </Well>

                            {hasNativeFcmFlagEnabled && <Well
                                    mode="ghost"
                                    hideFooter={true}
                                    title={(
                                        <span>
                                            <FirebaseIcon />
                                            <span>Android</span>
                                        </span>
                                    )}
                                >

                                        <NativeFcmIntegrationWell
                                            fcmConfig={this.props.domain?.nativeFcmConfiguration}
                                        />
                                </Well>
                            }
                        </>
                    )}

                    <SendIntegrationsDrawer
                        key={this.state.drawerKey}
                        visible={this.state.showDrawer}
                        onClose={this.handleDrawerClose}
                        domain={this.props.domain}
                        onSubmit={this.handleIntegrationsSubmit}
                        submitDisabled={this.submitDisabled}
                    />
                </Skeleton>
            </Well>
        );
    }

    protected handleDrawerOpen = async () => {
        return this.setState({
            showDrawer: true,
            drawerKey: randomstring.generate(),
        });
    }

    protected handleDrawerClose = async () => {
        return this.setState({
            showDrawer: false,
            drawerKey: undefined,
        });
    }

    protected getIntegrationsValuesUpdate(
        integrations: DomainSendIntegrationModel,
    ) {
        const domain = this.props.domain;
        const inactiveConfig = { is_active: false };
        const update: any = {};

        // convert case all serialized models to override current Domain DTO config values
        const newWebApnsConfig = convertCase(integrations.getWebApnsConfiguration().serialize(), 'camel');
        if (!deepEqual(domain.apnsConfiguration, newWebApnsConfig)) {
            if (newWebApnsConfig.isActive) {
                update.apnsConfiguration = {
                    ...newWebApnsConfig,
                    iconSourceUrl: domain.defaultIconUrl,
                    _cx: randomstring.generate(),
                }
            } else {
                update.apnsConfiguration = inactiveConfig;
            }
        }


        const newNativeApnsConfig = convertCase(integrations.getNativeApnsConfiguration().serialize(), 'camel');
        if (!deepEqual(domain.nativeApnsConfiguration, newNativeApnsConfig)) {
            if (newNativeApnsConfig.isActive) {
                update.nativeApnsConfiguration = newNativeApnsConfig;
            } else {
                update.nativeApnsConfiguration = inactiveConfig;
            }
        }

        const newNativeFcmConfig = convertCase(integrations.getNativeFcmConfiguration().serialize(), 'camel');
        if (!deepEqual(domain.nativeFcmConfiguration, newNativeFcmConfig)) {
            if (newNativeFcmConfig.isActive) {
                update.nativeFcmConfiguration = newNativeFcmConfig;
            } else {
                update.nativeFcmConfiguration = inactiveConfig;
            }
        }

        return update;
    }

    protected submitDisabled = (
        integrations: DomainSendIntegrationModel,
    ) => {
        return Object.keys(this.getIntegrationsValuesUpdate(integrations)).length === 0;
    }

    protected handleIntegrationsSubmit = async (
        integrations: DomainSendIntegrationModel,
    ) => {
        const update = this.getIntegrationsValuesUpdate(integrations);

        try {
            const res = await this.domainService.updateDomainById(this.props.domain.id, update);

            if (res.ok) {
                this.props.onDomainUpdated(res.data);

                return res;
            }
        } catch (error) {
            simpleFormErrorNotification(error);
        }

        return { ok: false };
    }

}



interface IWebApnsProps {
    channel: SendIntegrationChannel.WEB,
    apnsConfiguration: IWebApnsSendIntegration,
}

interface INativeApnsProps {
    channel: SendIntegrationChannel.NATIVE,
    apnsConfiguration: IAbstractNativeApnsSendIntegration
}

type ApnsProps = IWebApnsProps | INativeApnsProps;

export class ApnsIntegrationWell extends React.Component<ApnsProps, any> {

    protected appState: AppState;
    protected appService: AppService;
    protected domainService: DomainService;

    public constructor(props: ApnsProps) {
        super(props);

        this.appState = Container.get(AppState);
        this.appService = Container.get(AppService);
        this.domainService = Container.get(DomainService);
    }

    public render() {
        const apnsConfig = this.currentConfig;

        const enabled = apnsConfig?.getIsActive();
        const isNativeView = this.props.channel === SendIntegrationChannel.NATIVE;
        const firstSectionRenderValue = apnsConfig instanceof WebApnsConfigurationModel
            ? apnsConfig?.getPushPackage()?.website_push_id!
            : apnsConfig?.getAppBundleId()!;
        return (
            <div className="web-apns-well">
                <div className="integrations-section-title">
                    <span className={enabled ? 'enabled' : 'disabled'}>
                        {enabled ?
                            <CheckCircleOutlined />
                            :
                            <CloseCircleOutlined />}
                    </span>
                    <span>{this.props.channel}</span>
                </div>
                {enabled ?
                    (<>
                        {!(apnsConfig instanceof NativeP8ApnsConfiguration) && (
                            <>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                        {isNativeView ?
                                            'App Bundle ID'
                                            :
                                            'Web Push Id'
                                        }
                                    </div>
                                    <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                        <CopyToClipboard
                                            onCopy={() => this.handleCopied('website push id')}
                                            text={firstSectionRenderValue}
                                        >
                                            <span>{firstSectionRenderValue}</span>
                                        </CopyToClipboard>
                                    </div>
                                </div>

                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                        Expiration
                                    </div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {moment(apnsConfig?.getExpiration()).format('MMMM Do YYYY, h:mm:ss a')}
                                    </div>
                                </div>
                            </>
                        )}

                        {apnsConfig instanceof NativeP8ApnsConfiguration && (
                            <>
                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                        Team ID
                                    </div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {apnsConfig.getTeamId()}
                                    </div>
                                </div>

                                <div className={getClassNames('form-layout-row')}>
                                    <div className={getClassNames('form-layout-row-label')}>
                                    Key ID
                                    </div>
                                    <div className={getClassNames('form-layout-row-value')}>
                                        {apnsConfig.getKeyId()}
                                    </div>
                                </div>
                            </>
                        )}
                    </>)
                :
                    (
                        <div className={getClassNames('form-layout-row')}>
                           <div className={getClassNames('form-layout-row-label')}>
                               Not enabled
                           </div>
                        </div>
                    )
                }

            </div>
        );
    }

    protected get currentConfig(): NativeP8ApnsConfiguration | NativeP12ApnsConfiguration | WebApnsConfigurationModel | undefined {
        const config = this.props.apnsConfiguration;

        if (this.props.channel === SendIntegrationChannel.NATIVE) {
            if (this.props.apnsConfiguration.privateKeyType === SendIntegrationPrivateKeyType.P8) {
                return NativeP8ApnsConfiguration.build(config);
            } else if (this.props.apnsConfiguration.privateKeyType === SendIntegrationPrivateKeyType.P12) {
                return NativeP12ApnsConfiguration.build(config);
            }
        } else {
            return WebApnsConfigurationModel.build(config);
        }


        return;
    }

    protected handleCopied = (
        field: string,
    ) => {
        notification.destroy();
        simpleNotification('success', `Your ${field} has been copied!`);
    };
}

interface INativeFcmProps {
    fcmConfig: IAbstractNativeFcmSendIntegration,
}

export class NativeFcmIntegrationWell extends React.Component<INativeFcmProps, any> {

    protected appState: AppState;
    protected appService: AppService;
    protected domainService: DomainService;

    public constructor(props: INativeFcmProps) {
        super(props);

        this.appState = Container.get(AppState);
        this.appService = Container.get(AppService);
        this.domainService = Container.get(DomainService);
    }

    public render() {
        const fcmConfig = this.currentConfig;

        const enabled = fcmConfig?.getIsActive();
        return (
            <div>
                <div className="integrations-section-title">
                    <span className={enabled ? 'enabled' : 'disabled'}>
                        {enabled ?
                            <CheckCircleOutlined />
                            :
                            <CloseCircleOutlined />}
                        <span>NATIVE</span>
                    </span>
                </div>

                {enabled ? (
                    <>
                        <div className={getClassNames('form-layout-row')}>
                            <div className={getClassNames('form-layout-row-label')}>
                                Sender ID
                            </div>
                            <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                <CopyToClipboard
                                    onCopy={() => this.handleCopied('website push id')}
                                    text={fcmConfig?.getSenderId()!}
                                >
                                    <span>{fcmConfig?.getSenderId()}</span>
                                </CopyToClipboard>
                            </div>
                        </div>

                        {fcmConfig instanceof NativeV1FcmConfiguration &&
                            <div className={getClassNames('form-layout-row')}>
                                <div className={getClassNames('form-layout-row-label')}>
                                    Service Account JSON File URL
                                </div>
                                <div className={getClassNames('form-layout-row-value', 'can-copy')}>
                                    <CopyToClipboard
                                        onCopy={() => this.handleCopied('website push id')}
                                        text={fcmConfig?.getServiceAccountJsonFileUrl()!}
                                    >
                                        <span>{fcmConfig?.getServiceAccountJsonFileUrl()}</span>
                                    </CopyToClipboard>
                                </div>
                            </div>
                        }

                    </>
                    )
                        :
                    (
                    <div className={getClassNames('form-layout-row')}>
                        <div className={getClassNames('form-layout-row-label')}>
                            Not enabled
                        </div>
                    </div>
                    )
                }
            </div>
        )
    }

    protected get currentConfig(): NativeV1FcmConfiguration | NativeLegacyFcmConfiguration | undefined {
        const apiVersion = (this.props.fcmConfig as any).apiVersion;
        if (apiVersion === FcmSendIntegrationApiVersion.V1) {
            return NativeV1FcmConfiguration.build(this.props.fcmConfig);
        }

        if (apiVersion === FcmSendIntegrationApiVersion.LEGACY) {
            return NativeLegacyFcmConfiguration.build(this.props.fcmConfig);
        }

        return;
    }

    protected handleCopied = (
        field: string,
    ) => {
        notification.destroy();
        simpleNotification('success', `Your ${field} has been copied!`);
    };
}
